import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Globe, X, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const AUTH_TYPES = [
  { value: "none", label: "None" },
  { value: "bearer", label: "Bearer Token" },
  { value: "api_key", label: "API Key Header" },
  { value: "basic", label: "Basic Auth" },
  { value: "custom_header", label: "Custom Header" },
];

function genId() {
  return Math.random().toString(36).substr(2, 9);
}

function AuthConfig({ authType, authConfig, onChange }) {
  const set = (k, v) => onChange({ ...authConfig, [k]: v });
  if (authType === "bearer") {
    return (
      <div>
        <Label className="text-xs text-gray-500">Bearer Token</Label>
        <Input className="mt-1" type="password" value={authConfig.token || ""} onChange={e => set("token", e.target.value)} placeholder="eyJhbGci..." data-testid="bearer-token-input" />
      </div>
    );
  }
  if (authType === "api_key") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Header Name</Label>
          <Input className="mt-1" value={authConfig.header_name || "X-API-Key"} onChange={e => set("header_name", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Key Value</Label>
          <Input className="mt-1" type="password" value={authConfig.header_value || ""} onChange={e => set("header_value", e.target.value)} />
        </div>
      </div>
    );
  }
  if (authType === "basic") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Username</Label>
          <Input className="mt-1" value={authConfig.username || ""} onChange={e => set("username", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Password</Label>
          <Input className="mt-1" type="password" value={authConfig.password || ""} onChange={e => set("password", e.target.value)} />
        </div>
      </div>
    );
  }
  if (authType === "custom_header") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Header Name</Label>
          <Input className="mt-1" value={authConfig.custom_header_name || ""} onChange={e => set("custom_header_name", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Header Value</Label>
          <Input className="mt-1" type="password" value={authConfig.custom_header_value || ""} onChange={e => set("custom_header_value", e.target.value)} />
        </div>
      </div>
    );
  }
  return <p className="text-xs text-gray-400 italic">No authentication required.</p>;
}

function JsonTemplateEditor({ template, onChange, previewUploadId }) {
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(previewUploadId || "");
  const [sampleRow, setSampleRow] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [columns, setColumns] = useState([]);
  const [columnsMode, setColumnsMode] = useState("raw"); // "raw" | "transformed"

  useEffect(() => {
    client.get("/files/").then(r => {
      setUploads(r.data);
      if (!selectedUpload && r.data.length > 0) setSelectedUpload(r.data[0]._id);
    });
  }, []);

  useEffect(() => {
    if (!selectedUpload) return;
    setSampleRow(null);
    setColumns([]);
    setColumnsMode("raw");
    // Load raw columns
    client.get(`/files/${selectedUpload}`).then(r => {
      setColumns(r.data.columns?.map(c => c.name) || []);
    });
    // Load sample row from staging (raw)
    client.get(`/staging/${selectedUpload}/rows`, { params: { page: 1, per_page: 1 } }).then(r => {
      const row = r.data.rows?.[0];
      if (row) {
        const cleaned = { ...row };
        delete cleaned.upload_id;
        delete cleaned.row_index;
        setSampleRow(cleaned);
      }
    }).catch(() => {});
  }, [selectedUpload]);

  // Also try to load post-transformation columns via staging preview
  const loadTransformedColumns = async () => {
    if (!selectedUpload) return;
    try {
      const res = await client.get(`/staging/${selectedUpload}/preview`, { params: { limit: 1 } });
      const row = res.data.rows?.[0];
      if (row) {
        const cols = Object.keys(row).filter(k => !["upload_id","row_index"].includes(k));
        setColumns(cols);
        setSampleRow(row);
        setColumnsMode("transformed");
      }
    } catch (e) {}
  };

  const previewPayload = useCallback(() => {
    if (!sampleRow) { setPreviewError("Select a file with data to preview."); return; }
    setPreviewError("");
    try {
      const rendered = template.replace(/\{\{([^}]+)\}\}/g, (_, ref) => {
        const trimmed = ref.trim();
        const resolved = resolveRef(trimmed, sampleRow);
        if (resolved.found || resolved.isRaw) return serializeVal(resolved.val);
        const available = Object.keys(sampleRow).filter(k => !["upload_id","row_index"].includes(k)).slice(0, 6).join(", ");
        throw new Error(
          `Field {{${trimmed}}} not found in ${columnsMode} data.\n` +
          `Available: ${available}` +
          (columnsMode === "raw" ? "\nTip: click 'After transforms' to load post-pipeline fields." : "")
        );
      });
      const parsed = JSON.parse(rendered);
      setPreviewResult(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setPreviewError(e.message);
      setPreviewResult(null);
    }
  }, [template, sampleRow, columnsMode]); // eslint-disable-line

  // ── Field resolution helpers ──
  // Resolve {{ref}} against a row: exact match first, then case-insensitive, then prefix-strip
  const resolveRef = (ref, row) => {
    if (!row) return { found: false };
    if (ref.toLowerCase().startsWith("raw.")) return { found: true, val: null, isRaw: true };
    // Variable store references are resolved server-side — treat as valid
    if (ref.toLowerCase().startsWith("$var:")) return { found: true, val: `[${ref} — resolved server-side]`, isRaw: true };
    if (ref in row) return { found: true, val: row[ref] };
    const lRef = ref.toLowerCase();
    for (const [k, v] of Object.entries(row)) {
      if (k.toLowerCase() === lRef) return { found: true, val: v };
    }
    if (ref.includes(".")) {
      const afterFirst = ref.split(".").slice(1).join(".");
      if (afterFirst in row) return { found: true, val: row[afterFirst] };
      const lAfter = afterFirst.toLowerCase();
      for (const [k, v] of Object.entries(row)) {
        if (k.toLowerCase() === lAfter) return { found: true, val: v };
      }
    }
    return { found: false };
  };

  const serializeVal = (val) => {
    if (val === null || val === undefined) return "null";
    if (typeof val === "boolean") return val ? "true" : "false";
    if (typeof val === "number") return String(val);
    return JSON.stringify(String(val));
  };

  // All known column names (case-insensitive set)
  const allKnownCols = new Set([
    ...columns.map(c => c.toLowerCase()),
    ...(sampleRow
      ? Object.keys(sampleRow)
          .filter(k => !["upload_id", "row_index"].includes(k))
          .map(k => k.toLowerCase())
      : []),
  ]);

  // Refs from template — full reference (don't strip dot prefix)
  const rawRefs = [...template.matchAll(/\{\{([^}]+)\}\}/g)].map(m => m[1].trim());

  // Only warn about missing fields when we actually have column data to check against
  const hasColumnData = sampleRow !== null && (columns.length > 0 || Object.keys(sampleRow).length > 0);

  const missingFields = !hasColumnData ? [] : [...new Set(rawRefs.filter(ref => {
    if (ref.toLowerCase().startsWith("raw.")) return false;
    // Skip variable store references — these are resolved server-side
    if (ref.toLowerCase().startsWith("$var:")) return false;
    // 1. Exact case-insensitive match in known columns
    if (allKnownCols.has(ref.toLowerCase())) return false;
    // 2. Use full resolveRef logic (handles dotted column names like name.website)
    const res = resolveRef(ref, sampleRow);
    if (res.found || res.isRaw) return false;
    // 3. Prefix-stripped: {{table.fieldname}} → check fieldname in known cols
    if (ref.includes(".")) {
      const afterFirst = ref.split(".").slice(1).join(".");
      if (allKnownCols.has(afterFirst.toLowerCase())) return false;
    }
    return true;
  }))];

  const mightBeTransformed = missingFields.length > 0 && columnsMode === "raw";

  const insertPlaceholder = (col) => {
    onChange(template + `{{${col}}}`);
  };

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-700">
        <strong>JSON Template</strong> — Write the full JSON body.
        <ul className="mt-1 space-y-0.5 list-disc list-inside">
          <li><code className="bg-blue-100 px-1 rounded">{"{{fieldname}}"}</code> — transformed value (after pipeline)</li>
          <li><code className="bg-blue-100 px-1 rounded">{"{{raw.fieldname}}"}</code> — original raw value (before pipeline)</li>
          <li><code className="bg-blue-100 px-1 rounded">{"{{$VAR:KEY}}"}</code> — value from Variables Store (e.g. <code className="bg-blue-100 px-1 rounded">{"{{$VAR:BASE_URL}}"}</code>)</li>
          <li>Dotted column names like <code className="bg-blue-100 px-1 rounded">NAME.WEBSITE</code> → <code className="bg-blue-100 px-1 rounded">{"{{NAME.WEBSITE}}"}</code></li>
        </ul>
      </div>

      {/* Column chips */}
      {columns.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs text-gray-500">
              Available Columns — click to insert
            </Label>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${columnsMode === "transformed" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {columnsMode === "transformed" ? "post-transform" : "raw"}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {columns.map(col => (
              <button
                key={col}
                className="text-xs px-2 py-0.5 bg-[#e6f2f5] text-[#006080] border border-[#006080]/20 rounded hover:bg-[#006080] hover:text-white transition-colors font-mono"
                onClick={() => insertPlaceholder(col)}
                type="button"
              >
                {"{{"}{col}{"}}"}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Template editor */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">JSON Template Body</Label>
        <textarea
          className="w-full h-48 px-3 py-2 text-sm font-mono border border-gray-200 rounded-md focus:outline-none focus:border-[#006080] resize-none"
          value={template}
          onChange={e => onChange(e.target.value)}
          placeholder={'{\n  "name": "{{FirstName}} {{LastName}}",\n  "email": "{{Email}}",\n  "amount": {{Amount}}\n}'}
          spellCheck={false}
          data-testid="json-template-textarea"
        />
        {missingFields.length > 0 && hasColumnData && (
          <div className="mt-1.5 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-700">
            <div className="flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Unknown fields: </span>
                {missingFields.map(f => (
                  <code key={f} className="bg-amber-100 px-1 rounded mr-1 font-mono">{`{{${f}}}`}</code>
                ))}
                {mightBeTransformed && (
                  <span className="block mt-1">
                    If these are pipeline output columns (lookup results, calculated fields), click <strong>After transforms</strong> to reload with post-pipeline column names.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview section */}
      <div className="border border-gray-200 rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Live Preview</Label>
          <div className="flex items-center gap-2">
            <Select value={selectedUpload} onValueChange={setSelectedUpload}>
              <SelectTrigger className="h-6 w-44 text-xs"><SelectValue placeholder="Select file..." /></SelectTrigger>
              <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-[#006080] text-[#006080]" onClick={loadTransformedColumns} type="button" title="Reload columns after pipeline transforms">
              After transforms
            </Button>
            <Button size="sm" className="h-6 px-2 text-xs bg-[#006080] text-white hover:bg-[#004d66]" onClick={previewPayload} type="button">
              Preview
            </Button>
          </div>
        </div>
        <div className="text-xs text-[#006080] bg-[#e6f2f5] rounded px-2 py-1">
          <strong>Raw mode</strong>: shows columns before pipeline. Click <strong>After transforms</strong> to load post-pipeline columns (lookup results, calculated fields).
          Dotted column names (e.g. <code className="font-mono">name.website</code>), <code className="font-mono">{"{{raw.x}}"}</code>, and <code className="font-mono">{"{{$VAR:KEY}}"}</code> are never flagged as errors.
        </div>
        {previewError && (
          <div className="flex items-start gap-1.5 text-xs text-red-600 bg-red-50 rounded p-2">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>{previewError}</span>
          </div>
        )}
        {previewResult && (
          <div className="bg-gray-50 rounded p-2">
            <div className="flex items-center gap-1.5 text-xs text-green-600 mb-1">
              <CheckCircle size={12} />
              <span>Valid JSON — row 1 preview:</span>
            </div>
            <pre className="text-xs font-mono text-gray-700 overflow-x-auto">{previewResult}</pre>
          </div>
        )}
        {!previewResult && !previewError && sampleRow && (
          <div className="text-xs text-gray-400">Click Preview to validate template against first row of selected file.</div>
        )}
        {!sampleRow && selectedUpload && (
          <div className="text-xs text-gray-400">Loading sample data...</div>
        )}
      </div>
    </div>
  );
}

function ResponseTestPanel({ def, defId }) {
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState("");
  const [rowIndex, setRowIndex] = useState(0);
  const [dryRun, setDryRun] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get("/files/").then(r => {
      setUploads(r.data);
      if (r.data.length > 0) setSelectedUpload(r.data[0]._id);
    });
  }, []);

  const runTest = async () => {
    if (!defId) { setError("Save the API definition first before testing."); return; }
    if (!selectedUpload) { setError("Select a file to test with."); return; }
    setTesting(true); setError(""); setResult(null);
    try {
      const res = await client.post(`/api-defs/${defId}/test-call`, {
        upload_id: selectedUpload,
        row_index: parseInt(rowIndex) || 0,
        dry_run: dryRun,
      });
      setResult(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const downloadXlsx = () => {
    if (!selectedUpload) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/staging/${selectedUpload}/export-xlsx`, "_blank");
  };

  return (
    <div className="border border-[#006080]/20 rounded-md bg-[#F8F9FA] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-[#006080] uppercase tracking-wider">Test API Call</span>
        <span className="text-xs text-gray-400">— runs against a real row from your data</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-gray-500">File</Label>
          <Select value={selectedUpload} onValueChange={setSelectedUpload}>
            <SelectTrigger className="h-7 text-xs mt-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
            <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Row # (0-based)</Label>
          <Input className="h-7 text-xs mt-1" type="number" min={0} value={rowIndex} onChange={e => setRowIndex(e.target.value)} />
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer mb-0.5">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} className="w-3.5 h-3.5" />
            <span className="text-xs text-gray-600">Dry run</span>
          </label>
          <Button size="sm" className="h-7 px-3 text-xs bg-[#006080] text-white hover:bg-[#004d66] flex-1" onClick={runTest} disabled={testing}>
            {testing ? "Testing..." : "Run Test"}
          </Button>
        </div>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 rounded p-2">{error}</div>}

      {result && (
        <div className="space-y-2">
          {/* Status */}
          <div className="flex items-center gap-3">
            {result.status_code !== null && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${result.is_success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                HTTP {result.status_code}
              </span>
            )}
            {result.response_time_ms !== null && (
              <span className="text-xs text-gray-400">{result.response_time_ms}ms</span>
            )}
            {result.dry_run && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Dry Run</span>}
          </div>

          {/* Payload sent */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Payload Sent:</p>
            <pre className="text-xs font-mono bg-gray-900 text-green-400 rounded p-2 overflow-x-auto max-h-32">{JSON.stringify(result.payload, null, 2)}</pre>
          </div>

          {/* Response body */}
          {result.response_body && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Response Body:</p>
              <pre className="text-xs font-mono bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-48">{
                (() => { try { return JSON.stringify(JSON.parse(result.response_body), null, 2); } catch { return result.response_body; } })()
              }</pre>
            </div>
          )}

          {/* Extracted values */}
          {result.extracted_values && Object.keys(result.extracted_values).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Extracted Values:</p>
              {Object.entries(result.extracted_values).map(([col, val]) => (
                <div key={col} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-[#006080]">{col}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-mono text-gray-700">{String(val)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Download with response columns */}
          {selectedUpload && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-[#006080] text-[#006080]" onClick={downloadXlsx}>
              <CheckCircle size={12} className="mr-1.5" /> Download staging + response columns (xlsx)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ApiDefModal({ def, columns, onSave, onClose, editingDef }) {
  const [form, setForm] = useState(() => ({
    name: def?.name || "",
    description: def?.description || "",
    method: def?.method || "POST",
    base_url: def?.base_url || "",
    path: def?.path || "",
    query_params: def?.query_params || [],
    auth_type: def?.auth_type || "none",
    auth_config: def?.auth_config || {},
    headers: def?.headers || [],
    payload_mode: "json_template",
    payload_mapping: def?.payload_mapping || [],
    payload_json_template: def?.payload_json_template || "",
    response_config: def?.response_config || { success_codes: [200, 201], extract_field: "", write_to_column: "" },
    log_config: def?.log_config || { log_columns: [], concatenate_to_column: "" },
    webhook_url: def?.webhook_url || "",
    is_global: def?.is_global || false,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name || !form.base_url) { setError("Name and Base URL are required."); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  const addQP = () => set("query_params", [...form.query_params, { key: "", value: "", is_dynamic: false }]);
  const addHeader = () => set("headers", [...form.headers, { key: "", value: "" }]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="api-def-modal">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">{def ? "Edit" : "New"} API Definition</DialogTitle>
        </DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <Tabs defaultValue="endpoint">
          <TabsList className="h-auto">
            <TabsTrigger value="endpoint">Endpoint</TabsTrigger>
            <TabsTrigger value="auth">Auth</TabsTrigger>
            <TabsTrigger value="headers">Headers</TabsTrigger>
            <TabsTrigger value="payload">Payload</TabsTrigger>
            <TabsTrigger value="response">Response</TabsTrigger>
          </TabsList>

          <TabsContent value="endpoint" className="space-y-4 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">API Name *</Label>
                <Input className="mt-1" value={form.name} onChange={e => set("name", e.target.value)} placeholder="My API" data-testid="api-name-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Method</Label>
                <Select value={form.method} onValueChange={v => set("method", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{HTTP_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Base URL *</Label>
              <Input className="mt-1" value={form.base_url} onChange={e => set("base_url", e.target.value)} placeholder="https://api.example.com/v1" data-testid="api-base-url-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Path (use {"{col}"} for dynamic parts)</Label>
              <Input className="mt-1 font-mono text-sm" value={form.path} onChange={e => set("path", e.target.value)} placeholder="/users/{id}" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-gray-500">Query Parameters</Label>
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addQP}><Plus size={10} className="mr-1" /> Add</Button>
              </div>
              {form.query_params.map((qp, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <Input className="h-7 text-xs flex-1" value={qp.key} placeholder="param" onChange={e => {
                    const n = [...form.query_params]; n[i] = { ...qp, key: e.target.value }; set("query_params", n);
                  }} />
                  <Input className="h-7 text-xs flex-1" value={qp.value} placeholder="value" onChange={e => {
                    const n = [...form.query_params]; n[i] = { ...qp, value: e.target.value }; set("query_params", n);
                  }} />
                  <button onClick={() => set("query_params", form.query_params.filter((_, j) => j !== i))}><X size={12} className="text-gray-400" /></button>
                </div>
              ))}
            </div>

            {/* Webhook URL */}
            <div>
              <Label className="text-xs text-gray-500">Webhook URL on Completion (optional)</Label>
              <Input className="mt-1 font-mono text-sm" value={form.webhook_url || ""} onChange={e => set("webhook_url", e.target.value)} placeholder="https://your-service.com/webhook" />
              <p className="text-xs text-gray-400 mt-1">A POST request will be sent here when execution completes.</p>
            </div>

            {/* Global flag (super admin only) */}
            {editingDef?.isSuperAdmin !== false && (
              <div className="flex items-center gap-3 p-3 bg-[#e6f2f5] rounded-lg border border-[#006080]/20">
                <input type="checkbox" id="is_global_check" checked={form.is_global || false} onChange={e => set("is_global", e.target.checked)} className="w-4 h-4" />
                <label htmlFor="is_global_check" className="flex items-center gap-2 cursor-pointer text-sm font-medium text-[#006080]">
                  <ShieldCheck size={15} /> Make this a Global API Definition
                </label>
                <span className="text-xs text-gray-400">(visible to all companies)</span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="auth" className="space-y-4 pt-3">
            <div>
              <Label className="text-xs text-gray-500">Authentication Type</Label>
              <Select value={form.auth_type} onValueChange={v => set("auth_type", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{AUTH_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <AuthConfig authType={form.auth_type} authConfig={form.auth_config} onChange={v => set("auth_config", v)} />
          </TabsContent>

          <TabsContent value="headers" className="space-y-3 pt-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-gray-500">Request Headers</Label>
              <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addHeader}><Plus size={10} className="mr-1" /> Add</Button>
            </div>
            {form.headers.map((h, i) => (
              <div key={i} className="flex gap-2">
                <Input className="h-7 text-xs flex-1" value={h.key} placeholder="Header-Name" onChange={e => {
                  const n = [...form.headers]; n[i] = { ...h, key: e.target.value }; set("headers", n);
                }} />
                <Input className="h-7 text-xs flex-1" value={h.value} placeholder="value" onChange={e => {
                  const n = [...form.headers]; n[i] = { ...h, value: e.target.value }; set("headers", n);
                }} />
                <button onClick={() => set("headers", form.headers.filter((_, j) => j !== i))}><X size={12} className="text-gray-400" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="h-7 text-xs w-full" onClick={() => set("headers", [...form.headers, { key: "Content-Type", value: "application/json" }])}>
              + Add Content-Type: application/json
            </Button>
          </TabsContent>

          <TabsContent value="payload" className="space-y-3 pt-3">
            <JsonTemplateEditor
              template={form.payload_json_template || ""}
              onChange={v => set("payload_json_template", v)}
            />
          </TabsContent>

          <TabsContent value="response" className="space-y-4 pt-3">
            {/* Success codes */}
            <div>
              <Label className="text-xs text-gray-500">Success HTTP Codes (comma-separated)</Label>
              <Input
                className="mt-1 font-mono text-sm"
                value={(form.response_config.success_codes || [200, 201]).join(", ")}
                onChange={e => {
                  const codes = e.target.value.split(",").map(s => parseInt(s.trim())).filter(Boolean);
                  set("response_config", { ...form.response_config, success_codes: codes });
                }}
                placeholder="200, 201, 204"
              />
            </div>

            {/* Response field extractions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-xs text-gray-500">Response Field Extractions</Label>
                  <p className="text-xs text-gray-400 mt-0.5">Extract values from the API response and write to staging columns (auto-prefixed with <code className="font-mono">resp_</code>)</p>
                </div>
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => {
                  const exts = form.response_config.extractions || [];
                  set("response_config", { ...form.response_config, extractions: [...exts, { jsonpath: "", column: "" }] });
                }}>
                  <Plus size={10} className="mr-1" /> Add Field
                </Button>
              </div>
              {(form.response_config.extractions || []).map((ext, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <div className="flex-1">
                    <Input
                      className="h-7 text-xs font-mono"
                      value={ext.jsonpath}
                      onChange={e => {
                        const exts = [...(form.response_config.extractions || [])];
                        exts[i] = { ...exts[i], jsonpath: e.target.value };
                        set("response_config", { ...form.response_config, extractions: exts });
                      }}
                      placeholder="$.data.id"
                    />
                  </div>
                  <span className="text-gray-400 text-xs">→</span>
                  <div className="flex-1">
                    <Input
                      className="h-7 text-xs"
                      value={ext.column}
                      onChange={e => {
                        const exts = [...(form.response_config.extractions || [])];
                        exts[i] = { ...exts[i], column: e.target.value };
                        set("response_config", { ...form.response_config, extractions: exts });
                      }}
                      placeholder="resp_id (auto-prefixed if missing)"
                    />
                  </div>
                  <button onClick={() => {
                    const exts = (form.response_config.extractions || []).filter((_, j) => j !== i);
                    set("response_config", { ...form.response_config, extractions: exts });
                  }}>
                    <X size={12} className="text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              ))}
              {(!form.response_config.extractions || form.response_config.extractions.length === 0) && (
                <p className="text-xs text-gray-400 italic">No extractions. Add fields to map response values to staging columns.</p>
              )}
            </div>

            {/* Skip condition */}
            <div className="border border-amber-200 bg-amber-50 rounded-md p-4 space-y-3">
              <div>
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Skip Condition (Optional)</p>
                <p className="text-xs text-amber-600 mt-0.5">If a row already has a value in the specified column, skip it and don't reprocess. Useful for resuming partial runs.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Skip if column</Label>
                  <Input
                    className="mt-1 font-mono text-sm"
                    value={form.response_config.skip_condition?.column || ""}
                    onChange={e => set("response_config", {
                      ...form.response_config,
                      skip_condition: { ...form.response_config.skip_condition, column: e.target.value }
                    })}
                    placeholder="resp_title (leave empty to disable)"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Condition</Label>
                  <Select
                    value={form.response_config.skip_condition?.when || "not_empty"}
                    onValueChange={v => set("response_config", {
                      ...form.response_config,
                      skip_condition: { ...form.response_config.skip_condition, when: v }
                    })}
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_empty">is not empty (skip if already has a value)</SelectItem>
                      <SelectItem value="empty">is empty (only skip if blank)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Test API Panel */}
            <ResponseTestPanel def={form} defId={editingDef?._id} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving} data-testid="save-api-def-btn">
            {saving ? "Saving..." : "Save API Definition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApiDefinitionPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDef, setEditingDef] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadDefs(); }, []);

  const loadDefs = async () => {
    setLoading(true);
    const res = await client.get("/api-defs/").catch(() => ({ data: [] }));
    setDefs(res.data);
    setLoading(false);
  };

  const openEdit = (def) => { setEditingDef(def); setShowModal(true); };
  const openCreate = () => { setEditingDef(null); setShowModal(true); };

  const saveDef = async (form) => {
    if (editingDef) {
      await client.put(`/api-defs/${editingDef._id}`, form);
    } else {
      await client.post("/api-defs/", form);
    }
    await loadDefs();
  };

  const deleteDef = async (id) => {
    if (!window.confirm("Delete this API definition?")) return;
    await client.delete(`/api-defs/${id}`);
    loadDefs();
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} API definition(s)?`)) return;
    setDeleting(true);
    try {
      await client.post("/api-defs/bulk-delete", { ids: [...selected] });
      setSelected(new Set());
      await loadDefs();
    } finally { setDeleting(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const methodColor = { GET: "#059669", POST: "#2563EB", PUT: "#D97706", PATCH: "#7C3AED", DELETE: "#DC2626" };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">API Definitions</h1>
          <p className="text-gray-500 text-sm mt-0.5">Define the REST APIs you want to call with your data</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="outline" className="text-red-500 hover:bg-red-50 hover:border-red-200" onClick={bulkDelete} disabled={deleting}>
              <Trash2 size={14} className="mr-1.5" />{deleting ? "Deleting..." : `Delete ${selected.size} selected`}
            </Button>
          )}
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={openCreate} data-testid="create-api-def-btn">
            <Plus size={16} className="mr-2" /> New API Definition
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      ) : defs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Globe size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No API definitions yet</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={openCreate}>Create your first API definition</Button>
        </div>
      ) : (
        <>
          {defs.length > 1 && (
            <div className="flex items-center gap-3 px-2 py-1 mb-1">
              <input type="checkbox" className="w-4 h-4 rounded" checked={selected.size === defs.length} onChange={() => { if (selected.size === defs.length) setSelected(new Set()); else setSelected(new Set(defs.map(d => d._id))); }} />
              <span className="text-xs text-gray-400">{selected.size > 0 ? `${selected.size} selected` : "Select all"}</span>
            </div>
          )}
        <div className="space-y-3">
          {defs.map(def => (
            <div key={def._id} className={`bg-white border rounded-lg p-4 hover:shadow-sm transition-all ${selected.has(def._id) ? "border-[#006080] bg-[#F0F8FA]" : "border-gray-200"}`} data-testid={"api-def-" + def._id}>
              <div className="flex items-start gap-3">
                <input type="checkbox" className="w-4 h-4 rounded mt-0.5 flex-shrink-0" checked={selected.has(def._id)} onChange={() => toggleSelect(def._id)} onClick={e => e.stopPropagation()} />
                <div className="px-2 py-0.5 rounded text-xs font-bold text-white flex-shrink-0 mt-0.5" style={{ background: methodColor[def.method] || "#4B5563" }}>
                  {def.method}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{def.name}</p>
                  <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{def.base_url}{def.path}</p>
                  {def.description && <p className="text-xs text-gray-400 mt-1">{def.description}</p>}
                  <div className="flex gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs">{def.auth_type === "none" ? "No Auth" : def.auth_type}</Badge>
                    {def.payload_json_template ? (
                      <Badge variant="secondary" className="text-xs bg-[#e6f2f5] text-[#006080]">JSON Template</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">{def.payload_mapping?.length || 0} fields</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openEdit(def)} data-testid={"edit-api-def-" + def._id}>
                    <Edit2 size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:border-red-200" onClick={() => deleteDef(def._id)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {showModal && (
        <ApiDefModal
          def={editingDef}
          columns={[]}
          onSave={saveDef}
          editingDef={{ ...editingDef, isSuperAdmin }}
          onClose={() => { setShowModal(false); setEditingDef(null); }}
        />
      )}
    </div>
  );
}
